/**
 * Channel Detail Load RetailStore Reference Data Policy
 *
 * Triggered when a retailstore channel detail view is opened.
 * Loads available warehouses and price lists.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { ChannelsState } from '../state';
import { ChannelsCommand } from '../commands';

export const createChannelDetailLoadRetailStoreReferenceDataPolicy = (
  backend: ClientLibraryType
): Policy<ChannelsState, ChannelsCommand> => ({
  name: 'channel-detail-load-retailstore-reference-data',

  shouldReact: ( prev, next ) => {
    // Trigger when detail view opens (mode changes from closed to view) for a retailstore channel
    if ( prev.channelDetail.mode.type === 'closed' && next.channelDetail.mode.type === 'view' ) {
      const channel = next.salesChannels[next.channelDetail.mode.integrationId];
      return channel?.connectorId === 'retailstore';
    }
    return false;
  },

  onStateChange: async ({ next, ctx }) => {
    const { dispatch } = ctx;

    if ( next.channelDetail.mode.type !== 'view' ) return;

    // Load warehouses
    dispatch(
      { type: 'integrations/channels/detail/loading/setLoadingWarehouses', payload: { isLoading: true }},
      { source: 'policy' }
    );

    try {
      const warehouses = await backend.queries.StockManagementWarehouses();
      if ( warehouses ) {
        dispatch(
          { type: 'integrations/channels/detail/warehouses/set', payload: { warehouses }},
          { source: 'api' }
        );
      }
    } catch ( error ) {
      console.error( 'Error loading warehouses:', error );
    } finally {
      dispatch(
        { type: 'integrations/channels/detail/loading/setLoadingWarehouses', payload: { isLoading: false }},
        { source: 'policy' }
      );
    }

    // Load price lists
    dispatch(
      { type: 'integrations/channels/detail/loading/setLoadingPriceLists', payload: { isLoading: true }},
      { source: 'policy' }
    );

    try {
      const priceLists = await backend.queries.ItemsPublishingPriceLists();
      if ( priceLists ) {
        dispatch(
          { type: 'integrations/channels/detail/priceLists/set', payload: { priceLists }},
          { source: 'api' }
        );
      }
    } catch ( error ) {
      console.error( 'Error loading price lists:', error );
    } finally {
      dispatch(
        { type: 'integrations/channels/detail/loading/setLoadingPriceLists', payload: { isLoading: false }},
        { source: 'policy' }
      );
    }
  }
});
